import { z } from 'zod';
import { AkCertPemInputSchema, BaseResponseSchema, EkCertPemInputSchema, uuidSchema, } from './primitives.js';
import { DeviceInventoryStatusSchema, OpsDeviceCategorySchema, OpsDeviceDetailSchema, OpsDeviceRecordSchema, OpsDeviceStateHistoryEntrySchema, OpsIotProvisioningStatusSchema, } from './device-inventory.schemas.js';
/** Unified device record schema (consolidates IoT + non-IoT into one table) */
export const DeviceRecordSchema = OpsDeviceRecordSchema;
export const DeviceIdMappingSchema = z.object({
    pk: z.string(),
    serialNumber: z.string(),
    keyType: z.enum(['VENDOR_ID', 'MAC', 'VENDOR_SN']),
    createdAt: z.string(),
});
export const OpsFleetStatsSchema = z.object({
    not_provisioned: z.number().int().min(0),
    provisioned: z.number().int().min(0),
    total: z.number().int().min(0),
    unassigned: z.number().int().min(0).optional(),
    allDevices: z.object({
        total: z.number().int().min(0),
        inStock: z.number().int().min(0),
        allocated: z.number().int().min(0),
        shipped: z.number().int().min(0),
        claimed: z.number().int().min(0),
        active: z.number().int().min(0),
        returned: z.number().int().min(0),
        retired: z.number().int().min(0),
    }).optional(),
});
export const OpsFleetStatsByCategoryItemSchema = z.object({
    total: z.number().int().min(0),
    inStock: z.number().int().min(0),
    active: z.number().int().min(0),
    allocated: z.number().int().min(0).optional(),
    shipped: z.number().int().min(0).optional(),
    claimed: z.number().int().min(0).optional(),
    returned: z.number().int().min(0).optional(),
    retired: z.number().int().min(0).optional(),
});
export const OpsFleetStatsByCategorySchema = z.record(OpsDeviceCategorySchema, OpsFleetStatsByCategoryItemSchema);
export const OpsFleetStatsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        stats: OpsFleetStatsSchema,
        byCategory: OpsFleetStatsByCategorySchema.optional(),
    }),
});
export const OpsDeviceListQuerySchema = z.object({
    iotProvisioningStatus: OpsIotProvisioningStatusSchema.optional(),
    inventoryStatus: DeviceInventoryStatusSchema.optional(),
    deviceCategory: OpsDeviceCategorySchema.optional(),
    tenantId: z.string().optional(),
    vendorKey: z.string().optional(),
    mfgBatch: z.string().optional(),
    search: z.string().max(100).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const OpsDeviceListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        devices: z.array(OpsDeviceDetailSchema),
        nextCursor: z.string().nullable().optional(),
        count: z.number().int().min(0),
    }),
});
export const OpsDeviceDetailResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        device: OpsDeviceDetailSchema,
    }),
});
export const OpsAllocateDeviceRequestSchema = z.object({
    tenantId: uuidSchema,
    orderId: z.string().min(1).max(128),
});
export const OpsAllocateDeviceResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        serialNumber: z.string(),
        status: DeviceInventoryStatusSchema,
        allocatedAt: z.string(),
    }),
});
export const OpsUnallocateDeviceRequestSchema = z.object({
    reason: z.string().min(1).max(500),
});
export const OpsUnallocateDeviceResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        serialNumber: z.string(),
        status: DeviceInventoryStatusSchema,
    }),
});
export const OpsIotRevokeCertificateRequestSchema = z.object({
    reason: z.string().min(1),
});
export const OpsAllowReprovisionRequestSchema = z.object({
    reason: z.string().min(1),
});
export const OpsTriggerReprovisionRequestSchema = z.object({
    reason: z.string().min(1),
});
export const OpsAllowReprovisionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        device: OpsDeviceRecordSchema,
    }),
});
export const OpsTriggerReprovisionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        ssmCommandId: z.string().optional(),
        serialNumber: z.string(),
        triggeredAt: z.string(),
    }),
});
export const OpsRebootDeviceRequestSchema = z.object({
    reason: z.string().trim().min(1),
});
export const OpsRebootDeviceResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        ssmCommandId: z.string().optional(),
        serialNumber: z.string(),
        triggeredAt: z.string(),
    }),
});
export const OpsPeripheralProbeRequestSchema = z.object({
    reason: z.string().trim().min(1).max(500).optional(),
});
export const LocationSiteHealthAlertCandidateSchema = z.object({
    tenantId: z.string(),
    siteId: z.string(),
    siteName: z.string().nullable(),
    assignedCount: z.number().int().min(0),
    offlineCount: z.number().int().min(0),
    offlineRatio: z.number().min(0).max(1),
    gatewayConfigSyncHealth: z.string().nullable(),
    gatewayHeartbeatStatus: z.enum(['active', 'stale', 'unknown']).nullable(),
    tagsSeenCount: z.number().int().min(0).default(0),
    lowBatteryTagCount: z.number().int().min(0).default(0),
    neverSeenAssignedAnchorCount: z.number().int().min(0).default(0),
});
export const LocationSiteHealthAlertCandidatesResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        sites: z.array(LocationSiteHealthAlertCandidateSchema),
    }),
});
export { OpsRevealBreakGlassPasswordRequestSchema, OpsRevealBreakGlassPasswordResponseSchema, } from '../edge-break-glass.js';
export const OpsIotRevokeCertificateResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        serialNumber: z.string(),
        iotProvisioningStatus: OpsIotProvisioningStatusSchema,
        deactivatedAt: z.string(),
    }),
});
export const OpsResetDeviceResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        serialNumber: z.string(),
        iotProvisioningStatus: OpsIotProvisioningStatusSchema,
    }),
});
export const TpmChallengeRequestSchema = z.object({
    ekCertPem: EkCertPemInputSchema,
    akCertPem: AkCertPemInputSchema,
});
export const TpmChallengeResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        encryptedChallenge: z.string(),
    }),
});
export const OpsBulkAllocateRequestSchema = z.object({
    serialNumbers: z.array(z.string().min(1)).min(1).max(100),
    tenantId: uuidSchema,
    orderId: z.string().min(1).max(128),
});
export const OpsBulkAllocateResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        succeeded: z.array(z.string()),
        failed: z.array(z.object({
            serialNumber: z.string(),
            reason: z.string(),
        })),
    }),
});
export const OpsSerialNumberParamSchema = z.object({
    serialNumber: z.string().min(1).max(64),
});
export const OpsAuditLogResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        serialNumber: z.string(),
        stateHistory: z.array(OpsDeviceStateHistoryEntrySchema),
    }),
});
export const OpsRegisterIotDeviceRequestSchema = z.object({
    serialNumber: z.string().min(1).max(64).optional(),
    vendorSerialNumber: z.string().min(1).max(128),
    ekCertPem: EkCertPemInputSchema,
    akCertPem: AkCertPemInputSchema,
    manufacturerName: z.string().min(1).max(255).optional(),
    model: z.string().min(1).max(128),
    mfgBatch: z.string().min(1).max(64),
    mfgDate: z.string().refine((s) => /^\d{4}-\d{2}-\d{2}$/.test(s) || /^(1[0-2]|0?[1-9])\/(3[01]|[12][0-9]|0?[1-9])\/\d{4}$/.test(s), { message: 'mfgDate must be YYYY-MM-DD or M/D/YYYY' }),
    deviceCategory: z.enum(['efem-gateway', 'location-gateway']).default('efem-gateway'),
    macAddress: z.string().min(1).max(128).optional(),
    notes: z.string().max(500).optional(),
});
export const OpsRegisterNonIotDeviceRequestSchema = z.object({
    serialNumber: z.string().min(1).max(64).optional(),
    vendorSerialNumber: z.string().min(1).max(128),
    macAddress: z.string().min(1).max(128),
    deviceCategory: OpsDeviceCategorySchema.refine((c) => !['efem-gateway', 'location-gateway', 'multi-gateway'].includes(c), { message: 'Use IoT registration for gateway devices' }),
    manufacturerName: z.string().min(1).max(255).optional(),
    model: z.string().min(1).max(128),
    mfgBatch: z.string().min(1).max(64),
    mfgDate: z.string().refine((s) => /^\d{4}-\d{2}-\d{2}$/.test(s) || /^(1[0-2]|0?[1-9])\/(3[01]|[12][0-9]|0?[1-9])\/\d{4}$/.test(s), { message: 'mfgDate must be YYYY-MM-DD or M/D/YYYY' }),
    vendorDeviceId: z.string().max(128).optional(),
    notes: z.string().max(500).optional(),
});
export const OpsRegisterDeviceResponseSchema = BaseResponseSchema.extend({
    message: z.string(),
    data: z.object({
        device: OpsDeviceRecordSchema,
    }),
});
export const OpsDeviceMutationResponseSchema = BaseResponseSchema.extend({
    message: z.string(),
    data: z.object({
        device: OpsDeviceDetailSchema,
    }),
});
export const OpsProviderActivateResponseSchema = BaseResponseSchema.extend({
    message: z.string(),
    data: z.object({
        device: OpsDeviceRecordSchema,
    }),
});
/** PATCH non-IoT device metadata (`ops-devices.routes.ts`). */
export const OpsUpdateNonIotDeviceBodySchema = z.object({
    serialNumber: z.string().optional(),
    deviceCategory: z.string().optional(),
    manufacturerName: z.string().optional(),
    model: z.string().optional(),
    mfgBatch: z.string().optional(),
    mfgDate: z.string().optional(),
    macAddress: z.string().optional(),
    vendorSerialNumber: z.string().optional(),
    vendorDeviceId: z.string().optional(),
    notes: z.string().optional(),
    akCertPem: z.string().optional(),
    efemPeripheral: z.record(z.unknown()).nullable().optional(),
});
export const OpsUpdateNonIotDeviceResponseSchema = BaseResponseSchema.extend({
    message: z.string().optional(),
    data: z.object({
        device: z.unknown(),
    }),
});
//# sourceMappingURL=ops-devices.schemas.js.map